import { useMemo } from "react";
import {
  SkipBack, SkipForward, Play, Pause, ChevronLeft, ChevronRight, Clock,
} from "lucide-react";

const toMs = (iso) => (iso ? Date.parse(iso) : null);
const clock = (ms) => (ms ? new Date(ms).toISOString().replace("T", " ").slice(0, 19) : "—");
const SPEEDS = [1, 2, 5, 10];
const WINDOWS = [{ h: 6, l: "6h" }, { h: 24, l: "24h" }, { h: 72, l: "72h" }, { h: 168, l: "7j" }];

// BOTTOM — Time Machine (timeline horizontale type DAW). Replay uniquement des données réelles.
export default function TimeMachine({
  events, fromMs, toMs: endMs, tMs, onSeek, playing, onTogglePlay, speed, onSpeed,
  windowHours, onWindow, onPickDecision,
}) {
  const span = Math.max(1, (endMs || 0) - (fromMs || 0));
  const pct = (ms) => Math.min(100, Math.max(0, ((ms - fromMs) / span) * 100));
  const playPct = pct(tMs || fromMs);

  const markers = useMemo(() => (events || [])
    .map((e) => ({ ...e, ms: toMs(e.at) }))
    .filter((e) => e.ms != null && e.ms >= fromMs && e.ms <= endMs), [events, fromMs, endMs]);

  const decisionMs = useMemo(() =>
    markers.filter((m) => m.type === "decision" || m.type === "shadow").map((m) => m.ms).sort((a, b) => a - b),
    [markers]);

  const jump = (dir) => {
    if (dir === "start") return onSeek(fromMs);
    if (dir === "end") return onSeek(endMs);
    const cur = tMs || fromMs;
    if (dir === "prev") { const p = [...decisionMs].reverse().find((m) => m < cur - 500); onSeek(p ?? fromMs); }
    if (dir === "next") { const n = decisionMs.find((m) => m > cur + 500); onSeek(n ?? endMs); }
  };

  return (
    <section className="ck-panel" data-testid="ck-timemachine">
      <div className="ck-panel__head">
        <h2><Clock size={12} style={{ verticalAlign: "-1px", marginRight: 6 }} />Time Machine</h2>
        <span className="sub mono">{markers.length} événement(s) · fenêtre {WINDOWS.find((w) => w.h === windowHours)?.l || windowHours + "h"}</span>
      </div>
      <div className="ck-panel__body">
        <div className="tm">
          <div className="tm-controls">
            <button className="tm-btn" onClick={() => jump("start")} title="Début" data-testid="tm-start"><SkipBack size={15} /></button>
            <button className="tm-btn" onClick={() => jump("prev")} title="Décision précédente" data-testid="tm-prev"><ChevronLeft size={16} /></button>
            <button className={"tm-btn play" + (playing ? " on" : "")} onClick={onTogglePlay} title="Play/Pause" data-testid="tm-play">
              {playing ? <Pause size={16} /> : <Play size={16} />}
            </button>
            <button className="tm-btn" onClick={() => jump("next")} title="Décision suivante" data-testid="tm-next"><ChevronRight size={16} /></button>
            <button className="tm-btn" onClick={() => jump("end")} title="Fin" data-testid="tm-end"><SkipForward size={15} /></button>

            <div className="tm-speeds">
              {SPEEDS.map((s) => (
                <button key={s} className={"tm-speed" + (speed === s ? " on" : "")} onClick={() => onSpeed(s)} data-testid={`tm-speed-${s}`}>x{s}</button>
              ))}
            </div>
            <button className="tm-speed" onClick={() => { onSeek(endMs); }} data-testid="tm-now" style={{ borderColor: "#34d399", color: "#34d399" }}>NOW</button>

            <div className="tm-window">
              {WINDOWS.map((w) => (
                <button key={w.h} className={"tm-speed" + (windowHours === w.h ? " on" : "")} onClick={() => onWindow(w.h)} data-testid={`tm-window-${w.h}`}>{w.l}</button>
              ))}
            </div>
            <span className="tm-clock" data-testid="tm-clock">{clock(tMs)}</span>
          </div>

          <div className="tm-track">
            <div className="tm-ruler" />
            <div className="tm-progress" style={{ width: `${playPct}%` }} />
            {markers.map((m, i) => (
              <div key={m.decision_id ? m.decision_id + i : (m.audit_id || i)}
                className={"tm-marker " + m.type}
                style={{ left: `${pct(m.ms)}%` }}
                title={`${m.label || m.type} · ${clock(m.ms)}`}
                onClick={() => { onSeek(m.ms); if (m.decision_id) onPickDecision(m.decision_id); }}
                data-testid={`tm-marker-${m.type}`} />
            ))}
            <div className="tm-playhead" style={{ left: `${playPct}%` }} />
            <input className="tm-scrub" type="range" min={fromMs} max={endMs} step={Math.max(1000, Math.round(span / 1000))}
              value={tMs || fromMs} onChange={(e) => onSeek(Number(e.target.value))} data-testid="tm-scrubber" aria-label="Curseur temporel" />
          </div>

          <div className="tm-labels">
            <span>{clock(fromMs)}</span>
            <span>{clock(endMs)}</span>
          </div>
          <div className="tm-legend">
            <span><i className="tm-marker decision" style={{ position: "static", transform: "none", height: 12 }} />décision</span>
            <span><i className="tm-marker shadow" style={{ position: "static", transform: "none", height: 12 }} />shadow</span>
            <span><i className="tm-marker incident" style={{ position: "static", transform: "none", height: 12, top: 0 }} />incident</span>
            <span><i className="tm-marker assign" style={{ position: "static", transform: "none", height: 12 }} />affectation</span>
            <span><i className="tm-marker recalc" style={{ position: "static", transform: "none", height: 12 }} />recalcul</span>
            <span><i className="tm-marker pickup" style={{ position: "static", transform: "none", height: 12 }} />pickup/dropoff</span>
          </div>
        </div>
      </div>
    </section>
  );
}
